import type { VariantStock } from "@cleanbudget/contract/stock";
import { IconDeviceMobile, IconMapPin } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, stripSearchParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ButtonLink } from "@/components/button-link";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { DataTableEmpty } from "@/components/data-table/empty";
import { DataTablePagination } from "@/components/data-table/pagination";
import { DataTableToolbar, FilterSelect, SearchInput } from "@/components/data-table/toolbar";
import { PageHeader } from "@/components/page-header";
import { orpc } from "@/lib/orpc";
import { LIST_DEFAULTS, StockSearch } from "./-components/search";
import { StockLevelBadge } from "./-components/stock-level";

const stockQuery = (search: StockSearch) => orpc.stock.list.queryOptions({ input: search });
const locationsQuery = () => orpc.stock.listLocations.queryOptions();

/**
 * Voorraad (#62): every variant with its stock per location, its total and what can still be sold.
 * Search (name, SKU, EAN), the low / out filter and the page live in the URL. A row opens the
 * variant's stock page, where movements are booked and the history shows.
 */
export const Route = createFileRoute("/_admin/stock/")({
	validateSearch: StockSearch,
	search: { middlewares: [stripSearchParams(LIST_DEFAULTS)] },
	loaderDeps: ({ search }) => search,
	loader: ({ context, deps }) =>
		Promise.all([
			context.queryClient.ensureQueryData(stockQuery(deps)),
			context.queryClient.ensureQueryData(locationsQuery()),
		]),
	component: StockPage,
});

function StockPage() {
	const { t } = useTranslation("stock");
	const search = Route.useSearch();
	const { data } = useSuspenseQuery(stockQuery(search));
	const { data: locations } = useSuspenseQuery(locationsQuery());
	// With one location its column says the same as the total: show it only once there are more.
	const perLocation = locations.length > 1;

	const columns: readonly Column<VariantStock>[] = [
		{
			id: "variant",
			header: t("column.variant"),
			cell: (stock) => (
				<span className="flex min-w-36 flex-col whitespace-normal">
					<Link
						to="/stock/$variantId"
						params={{ variantId: stock.variantId }}
						className="font-medium underline-offset-4 hover:underline"
					>
						{stock.productName}
					</Link>
					{stock.variantName === stock.productName ? null : (
						<span className="text-sm text-muted-foreground">{stock.variantName}</span>
					)}
					<span className="font-mono text-xs break-all text-muted-foreground">
						{stock.sku}
						{stock.ean ? <span className="hidden md:inline"> · {t("ean", { ean: stock.ean })}</span> : null}
					</span>
				</span>
			),
		},
		...(perLocation
			? locations.map(
					(location): Column<VariantStock> => ({
						id: `location-${location.id}`,
						header: location.name,
						secondary: true,
						align: "right",
						cell: (stock) => (
							<span className="tabular-nums">
								{stock.levels.find((level) => level.locationId === location.id)?.quantity ?? 0}
							</span>
						),
					}),
				)
			: []),
		{
			id: "total",
			header: t("column.total"),
			align: "right",
			cell: (stock) => <span className="font-medium tabular-nums">{stock.quantity}</span>,
		},
		{
			id: "available",
			header: t("column.available"),
			align: "right",
			secondary: true,
			cell: (stock) => <span className="tabular-nums">{stock.available}</span>,
		},
		{
			id: "status",
			header: t("column.status"),
			cell: (stock) => (
				<span className="flex flex-wrap gap-1">
					<StockLevelBadge stock={stock} />
					{stock.active ? null : (
						<span className="hidden text-xs text-muted-foreground md:inline">{t("inactive")}</span>
					)}
				</span>
			),
		},
	];

	const filtered = Boolean(search.search) || search.stock !== undefined || search.activeOnly !== undefined;
	const pastEnd = data.items.length === 0 && data.total > 0;

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={t("title")}
				description={t("description")}
				actions={
					<>
						<ButtonLink to="/settings/location" variant="outline">
							<IconMapPin aria-hidden />
							{t("locationsLink")}
						</ButtonLink>
						<ButtonLink to="/stock/quick">
							<IconDeviceMobile aria-hidden />
							{t("quickLink")}
						</ButtonLink>
					</>
				}
			/>
			<div className="flex flex-col gap-3">
				<DataTableToolbar>
					<SearchInput value={search.search} label={t("searchLabel")} />
					<FilterSelect
						param="stock"
						label={t("filter.label")}
						value={search.stock}
						options={[
							{ value: "low", label: t("filter.low") },
							{ value: "out", label: t("filter.out") },
						]}
					/>
					<FilterSelect
						param="activeOnly"
						label={t("filter.activeLabel")}
						value={search.activeOnly}
						options={[{ value: "yes", label: t("filter.activeOnly") }]}
					/>
				</DataTableToolbar>
				<DataTable
					label={t("title")}
					columns={columns}
					rows={data.items}
					rowKey={(stock) => stock.variantId}
					empty={<DataTableEmpty filtered={filtered} pastEnd={pastEnd} nothingYet={t("empty")} />}
				/>
				<DataTablePagination page={data.page} pageSize={data.pageSize} total={data.total} />
			</div>
		</div>
	);
}
